/**
 * 게시판 관리 API client (migration 102, 2026-08-18).
 *
 * chat-server 의 `/api/v1/admin/boards*`.
 *
 * ── 통합설정 › 회사 관리 (2026-08-25) ─────────────────────────────────────────
 * 관리 콘솔(8444)의 같은 이름 api 모듈을 **그대로 옮겨 온 것**이다. 함수 이름·인자·반환 모양이
 * 한 글자도 다르지 않다 — 두 화면이 같은 서버 라우트를 부르므로 원본과 대조하기 쉽게 둔다.
 * 콘솔은 BFF `/api` + 이 상대 경로, 8443 은 baseURL 이 이미 `/api/v1` 이라 **같은 문자열이
 * 같은 라우트에 닿는다**(서버·BFF 변경 0). 권한은 chat-server 가 ADMIN/SYSTEM_ADMIN 으로 판정.
 */
import { apiClient } from './client';

export type BoardType = 'CLASSIC' | 'FEED';
export type BoardScope = 'COMPANY' | 'DIVISION' | 'TEAM' | 'PERSONAL';
export type ShareTargetType = 'company' | 'division' | 'team' | 'user';

export interface AdminBoardRow {
  id: string;
  name: string;
  description: string | null;
  boardType: BoardType;
  scopeType: BoardScope;
  scopeId: string | null;
  scopeName: string | null;
  systemKey: string | null;
  isPrivate: boolean;
  showSecretTitles: boolean;
  allowAnonymous: boolean;
  showMailButton: boolean;
  allowComments: boolean;
  usePostReaders: boolean;
  notifyEnabled: boolean;
  notifyAllByDefault: boolean;
  sortOrder: number;
  createdAt: string;
  postCount: number;
  memberCount: number;
  owner: { id: string; name: string; email: string } | null;
}

export interface BoardSettings {
  defaultNotifyAll: boolean;
  allowUserBoardCreate: boolean;
  updatedAt: string | null;
}

export interface BoardShare {
  targetType: ShareTargetType;
  targetId: string;
  canRead: boolean;
  canNotify: boolean;
}

export interface AdminBoardPatch {
  name?: string;
  description?: string | null;
  boardType?: BoardType;
  isPrivate?: boolean;
  showSecretTitles?: boolean;
  allowAnonymous?: boolean;
  showMailButton?: boolean;
  allowComments?: boolean;
  usePostReaders?: boolean;
  notifyEnabled?: boolean;
  notifyAllByDefault?: boolean;
  sortOrder?: number;
}

export async function listAdminBoards(): Promise<{ boards: AdminBoardRow[]; settings: BoardSettings }> {
  const { data } = await apiClient.get<{ boards: AdminBoardRow[]; settings: BoardSettings }>('/admin/boards');
  return {
    boards: Array.isArray(data?.boards) ? data.boards : [],
    settings: data?.settings ?? { defaultNotifyAll: true, allowUserBoardCreate: true, updatedAt: null },
  };
}

export async function getAdminBoard(id: string): Promise<{ board: AdminBoardRow; shares: BoardShare[] }> {
  const { data } = await apiClient.get<{ board: AdminBoardRow; shares: BoardShare[] }>(`/admin/boards/${id}`);
  return { board: data.board, shares: Array.isArray(data?.shares) ? data.shares : [] };
}

export async function updateAdminBoard(id: string, patch: AdminBoardPatch): Promise<AdminBoardRow> {
  const { data } = await apiClient.patch<{ board: AdminBoardRow }>(`/admin/boards/${id}`, patch);
  return data.board;
}

export async function deleteAdminBoard(id: string): Promise<void> {
  await apiClient.delete(`/admin/boards/${id}`);
}

export async function getBoardSettings(): Promise<BoardSettings> {
  const { data } = await apiClient.get<{ settings: BoardSettings }>('/admin/board-settings');
  return data.settings;
}

export async function updateBoardSettings(patch: Partial<Omit<BoardSettings, 'updatedAt'>>): Promise<BoardSettings> {
  const { data } = await apiClient.patch<{ settings: BoardSettings }>('/admin/board-settings', patch);
  return data.settings;
}
